import { take, takeEvery, put, call } from "redux-saga/effects";
import { createAction } from "@reduxjs/toolkit";

import { closeModal, openModal, registerModal } from "libs/modals";
import { trackEventFx } from "libs/analytics";
import { POST_DUPLICATE } from "constants/Events";
import { Post } from "./types";

interface openCreatePostModalProps {
  type?: string;
  accounts?: unknown[];
  assets?: any[];
  caption?: string;
  labelIds?: string[];
  post?: Partial<Post>;
  duplicatePostsCount?: number;
  callback?: (action: { payload?: { saved?: boolean } }) => void;
}

const CREATE_POST_MODAL = "CREATE_POST_MODAL";

// Lazy registration to avoid circular dependency with CreatePostModal
let modalRegistration: Promise<void> | undefined;
function ensureModalRegistered(): Promise<void> {
  modalRegistration ??= import("./components/CreatePostModal")
    .then(({ CreatePostModal }) => {
      registerModal(CREATE_POST_MODAL, CreatePostModal);
    })
    .catch((error) => {
      modalRegistration = undefined;
      throw error;
    });
  return modalRegistration;
}

export const openCreatePostModal = createAction<openCreatePostModalProps>(
  "OPEN_CREATE_POST_MODAL"
);
export const closeCreatePostModal = createAction("CLOSE_CREATE_POST_MODAL");

function* createPostModalSaga({ payload }: any): Generator<unknown, void, any> {
  const { callback, duplicatePostsCount, ...props } = payload ?? {};

  // Ensure modal is registered before opening
  yield call(ensureModalRegistered);

  if (duplicatePostsCount && duplicatePostsCount > 0) {
    yield call(trackEventFx, {
      eventName: POST_DUPLICATE,
      metadata: { posts: duplicatePostsCount },
    });
  }

  yield put(
    openModal({
      type: CREATE_POST_MODAL,
      props,
    })
  );

  const formData = yield take(closeCreatePostModal.type);
  yield put(
    closeModal({
      type: CREATE_POST_MODAL,
    })
  );
  callback?.(formData);
}

export function* createPostSaga() {
  yield takeEvery(openCreatePostModal.type, createPostModalSaga);
}
